<template>
  <div :class="rootClass">
    <div class="context-files__section">
      <div class="context-files__header">
        <div class="context-files__header-left">
          <p class="context-files__title">Files</p>
          <span class="context-files__count-badge">{{ countLabel }}</span>
        </div>
        <button
          v-if="showUploadButton"
          type="button"
          class="context-files__upload-btn"
          :disabled="disabled"
          @click="triggerUpload"
        >
          <svg
            class="context-files__upload-icon"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 19V5M5 12l7-7 7 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>Upload file</span>
        </button>
      </div>

      <div v-if="!isEmpty" class="context-files__grid">
        <div
          v-for="file in uploadingFiles"
          :key="`uploading-${file.name}`"
          class="context-files__card context-files__card--uploading"
        >
          <p class="context-files__card-name" :title="file.name">{{ file.name }}</p>
          <div class="context-files__progress">
            <div
              class="context-files__progress-fill"
              :style="{ width: `${file.progress || 0}%` }"
            />
          </div>
        </div>
        <div
          v-for="file in files"
          :key="file.id || file.name"
          class="context-files__card"
          :class="{ 'context-files__card--openable': openable }"
          :tabindex="openable ? 0 : undefined"
          :title="openable ? 'Click to preview' : undefined"
          @click="openFileFromEvent(file, $event)"
          @keydown.enter="openFileFromKeydown(file, $event)"
          @keydown.space="openFileFromKeydown(file, $event)"
        >
          <p class="context-files__card-name" :title="file.name">{{ file.name }}</p>
          <div class="context-files__card-footer">
            <span class="context-files__badge">{{ getFileBadge(file) }}</span>
            <button
              v-if="canEdit"
              type="button"
              class="context-files__remove-btn"
              :aria-label="`Remove ${file.name}`"
              :disabled="disabled"
              @click="emit('remove', file.id)"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      multiple
      :accept="accept"
      class="context-files__input"
      aria-hidden="true"
      tabindex="-1"
      @change="onFileChange"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const FILE_TYPE_LABELS = {
  pdf: "PDF",
  doc: "DOC",
  docx: "DOCX",
  txt: "TXT",
  csv: "CSV",
  xlsx: "XLSX",
};

const props = defineProps({
  files: {
    type: Array,
    default: () => [],
  },
  maxFiles: {
    type: Number,
    default: 20,
  },
  className: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // When false, upload and remove controls are hidden (read-only viewers).
  canEdit: {
    type: Boolean,
    default: true,
  },
  // Native file input accept attribute; override for Market Data (PDF/DOCX/XLSX/CSV).
  accept: {
    type: String,
    default:
      ".pdf,.doc,.docx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv",
  },
  // Files mid-upload, rendered as pending cards with a progress bar: { name, progress }.
  uploadingFiles: {
    type: Array,
    default: () => [],
  },
  // When true, cards emit open-file on click / Enter / Space so the host can
  // show a reader (e.g. FileReaderModal). Kept opt-in so read-only list hosts
  // (Market Data files, etc.) keep their current single-purpose card behaviour.
  openable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["upload", "remove", "open-file"]);

const fileInput = ref(null);

const rootClass = computed(() => props.className || "context-files");
const isEmpty = computed(() => props.files.length === 0 && props.uploadingFiles.length === 0);
const countLabel = computed(() => `${props.files.length} of ${props.maxFiles}`);
const isAtCapacity = computed(() => props.files.length >= props.maxFiles);
const showUploadButton = computed(() => props.canEdit && !isAtCapacity.value);

function getFileExtension(fileName) {
  const match = /\.([a-zA-Z0-9]+)$/.exec(fileName || "");
  return match ? match[1].toLowerCase() : "";
}

function getFileBadge(file) {
  const ext = String(file.type || getFileExtension(file.name)).toLowerCase();
  return FILE_TYPE_LABELS[ext] || ext.toUpperCase();
}

function triggerUpload() {
  if (props.disabled || !showUploadButton.value) return;
  fileInput.value && fileInput.value.click();
}

function onFileChange(event) {
  const uploadedFiles = event.target.files;
  if (uploadedFiles && uploadedFiles.length && !props.disabled && props.canEdit) {
    emit("upload", uploadedFiles);
  }
  event.target.value = "";
}

function openFileFromEvent(file, event) {
  // A click on the remove ("x") button must keep its remove semantics and
  // never open the reader (ER-11347; single-click open per ER-11428).
  if (event && event.target && event.target.closest(".context-files__remove-btn")) {
    return;
  }
  openFile(file);
}

function openFileFromKeydown(file, event) {
  // Enter/Space on the remove button must keep its native activation (remove),
  // never open the reader (ER-11347). The keydown listener sits on the card and
  // bubbles, so it must be skipped for button-originated events.
  if (event && event.target && event.target.closest(".context-files__remove-btn")) {
    return;
  }
  if (openFile(file) && event) {
    // Only stop the default action (e.g. Space scrolling the page) when the
    // keystroke actually opens the reader.
    event.preventDefault();
  }
}

function openFile(file) {
  if (!props.openable || props.disabled) return false;
  emit("open-file", file);
  return true;
}
</script>

<style scoped>
.context-files {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 16px 0 17px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.context-files__section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
}

.context-files__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 15px;
  width: 100%;
}

.context-files__header-left {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.context-files__title {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #101828;
  white-space: nowrap;
  margin: 0;
}

.context-files__count-badge {
  display: inline-flex;
  align-items: flex-start;
  padding: 1px 7px;
  border-radius: 10px;
  background: #f2f4f7;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 500;
  color: #667085;
  white-space: nowrap;
  flex-shrink: 0;
}

.context-files__upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}

.context-files__upload-btn:hover {
  background: #f9fafb;
}

.context-files__upload-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.context-files__upload-btn:disabled,
.context-files__remove-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.context-files__upload-icon {
  display: block;
  flex-shrink: 0;
}

.context-files__grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  padding: 0 10px;
  width: 100%;
}

.context-files__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 272px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  flex-shrink: 0;
}

.context-files__card-name {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  margin: 0;
}

.context-files__card--openable {
  cursor: pointer;
}

.context-files__card--openable:hover {
  border-color: #d6bbfb;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.context-files__card--openable:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.context-files__card--uploading {
  border-style: dashed;
}

.context-files__progress {
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: #f2f4f7;
  overflow: hidden;
}

.context-files__progress-fill {
  height: 100%;
  background: #7f56d9;
  transition: width 0.2s ease;
}

.context-files__badge {
  display: inline-flex;
  align-items: flex-start;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f2edff;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #7f56d9;
  white-space: nowrap;
}

.context-files__card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.context-files__remove-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  color: #667085;
  cursor: pointer;
  flex-shrink: 0;
}

.context-files__remove-btn:hover {
  color: #d92d20;
}

.context-files__remove-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.context-files__input {
  display: none;
}
</style>
